import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

export interface SparkPoint {
  week: string;
  value: number;
}

export interface KpiMetric {
  total: number;
  sparkline: SparkPoint[];
  prior: number;
  delta_pct: number;
}

export interface FinanceKpis {
  revenue: KpiMetric;
  expenses: KpiMetric;
  net_income: KpiMetric;
  margin_pct: number;
  dollar_per_stop: number;
  labor_per_stop: number;
  fuel_recovery_pct: number;
  total_stops: number;
  chargebacks: number;
}

export interface RevenueByIsp {
  isp_number: string;
  terminal?: string;
  net_settlement: number;
  chargebacks: number;
  stops: number;
  dollar_per_stop: number;
}

export interface PnlWeek {
  week_ending: string;
  revenue: number;
  expenses: number;
  net: number;
}

export interface ActionItem {
  severity: 'flagged' | 'caution' | 'info';
  text: string;
  dollar_impact?: number | null;
  report?: string;
}

export interface FinancePeriod {
  type: string;
  start_date?: string;
  end_date?: string;
  week_endings?: string[];
  weeks_included: number;
  available_weeks?: string[];
}

export interface FinanceSummaryData {
  period: FinancePeriod;
  kpis: FinanceKpis;
  revenue_by_isp: RevenueByIsp[];
  pnl_weekly: PnlWeek[];
  action_items: ActionItem[];
}

interface SummaryResponse extends FinanceSummaryData {
  error?: string;
}

export function useFinanceSummary(periodType: string) {
  const [data, setData] = useState<FinanceSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (periodType) params.set('period', periodType);

      const resp = await apiFetch<SummaryResponse>(`/api/finance/summary?${params.toString()}`);
      if (resp?.error) throw new Error(resp.error);

      setData(resp);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Finance summary load failed');
    } finally {
      setLoading(false);
    }
  }, [periodType]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refreshSummary: load };
}
